package dev.kuml.portal.web.editor

import dev.kuml.portal.web.AppScope
import dev.kuml.portal.web.api.ApiResult
import dev.kuml.portal.web.api.PortalApi
import dev.kuml.portal.web.api.ProjectFileResponse
import dev.kuml.portal.web.i18n.t
import io.kvision.core.Container
import io.kvision.core.onClick
import io.kvision.form.text.TextInput
import io.kvision.form.text.textInput
import io.kvision.html.Div
import io.kvision.html.button
import io.kvision.html.div
import io.kvision.html.image
import io.kvision.html.link
import io.kvision.html.span
import kotlinx.browser.document
import kotlinx.coroutines.launch
import org.w3c.dom.HTMLInputElement
import org.w3c.files.File

/** @property reload re-fetches the file list from the server -- exposed so a future caller (e.g.
 *   a diagram switch) can refresh without recreating the whole panel; not currently called from
 *   outside [projectFilesPanel] itself but kept symmetric with [DiagramTabsHandle]'s own shape. */
public class ProjectFilesPanelHandle(
    public val reload: () -> Unit,
)

/**
 * R-26's persistent "Projekt-Dateien" panel -- an einklappbarer Bereich unterhalb des DSL-Panels
 * im Editor (Entscheidung D-12 des Plans: kein eigener Screen, das Panel sitzt dort, wo der
 * KI-Assistent bedient wird). Mirrors [diagramTabs]' "self-contained widget with a reload()
 * handle" shape and [EditorScreen.kt]'s established R-8 attach-file pattern (hidden native
 * `<input type="file">`, created via `document.createElement` and attached to its own host node's
 * real DOM element -- KVision has no dedicated file-picker widget, and this NEVER goes through
 * `innerHTML`).
 *
 * Every server-supplied value (file name, description, INDEX.md text) is rendered EXCLUSIVELY via
 * KVision's plain (non-`rich`) `content =` / widget-constructor text parameters, which set
 * `textContent`, never HTML -- this codebase's binding convention for user-controlled free text,
 * doubly important here since a file name/description is chosen by whichever team member uploaded
 * it, not necessarily the viewer.
 *
 * Client-side kind/size checks ([ProjectFileKindClient]) are pure convenience, exactly like R-8's
 * [AiContextFile] -- the load-bearing validation is `dev.kuml.portal.server.files.ProjectFileValidator`
 * server-side; every error this panel surfaces ultimately came back from the server.
 */
public fun Container.projectFilesPanel(
    api: PortalApi,
    projectId: String,
): ProjectFilesPanelHandle {
    lateinit var listHost: Div
    lateinit var toggle: Div
    lateinit var body: Div
    lateinit var uploadStatus: Div
    var expanded = false
    var currentFiles: List<ProjectFileResponse> = emptyList()

    val root = div(className = "kportal-files-panel")
    toggle = root.div(panelToggleLabel(expanded, 0), className = "kportal-files-toggle")
    body = root.div(className = "kportal-files-body kportal-hidden")

    listHost = body.div()

    val uploadRow = body.div(className = "kportal-attach-row")
    val uploadButton =
        uploadRow.button(t.projectFiles.uploadButton, className = "kportal-btn kportal-btn-text-neutral kportal-btn-auto")
    val descriptionInput = uploadRow.textInput(className = "kportal-input kportal-files-desc-input")
    descriptionInput.placeholder = t.projectFiles.descriptionPlaceholder
    descriptionInput.maxlength = ProjectFileKindClient.MAX_DESCRIPTION_LENGTH

    val fileInput = document.createElement("input") as HTMLInputElement
    fileInput.type = "file"
    fileInput.accept = ProjectFileKindClient.ACCEPT_ATTRIBUTE
    fileInput.style.display = "none"
    uploadRow.getElement()?.appendChild(fileInput)

    uploadStatus = body.div("", className = "kportal-status-area")
    uploadStatus.visible = false

    val indexLink = body.div(t.projectFiles.viewIndexLink, className = "kportal-link")
    val indexHost = body.div(className = "kportal-files-index kportal-hidden")

    fun reload() {
        AppScope.launch {
            when (val result = api.listProjectFiles(projectId)) {
                is ApiResult.Success -> {
                    currentFiles = result.value
                    toggle.content = panelToggleLabel(expanded, currentFiles.size)
                    listHost.removeAll()
                    if (currentFiles.isEmpty()) {
                        listHost.div(t.projectFiles.emptyHint, className = "kportal-attach-hint")
                    } else {
                        currentFiles.forEach { file -> renderFileRow(listHost, api, file) { reload() } }
                    }
                }
                // Non-critical: the panel simply keeps whatever it last rendered (matches
                // DiagramTabs' own "silently keep the stale list on transient errors" posture for
                // its own reload() -- this list is a convenience view, not the source of truth).
                is ApiResult.HttpError, ApiResult.NetworkError -> Unit
            }
        }
    }

    toggle.onClick {
        expanded = !expanded
        toggle.content = panelToggleLabel(expanded, currentFiles.size)
        if (expanded) body.removeCssClass("kportal-hidden") else body.addCssClass("kportal-hidden")
    }

    uploadButton.onClick { fileInput.click() }
    fileInput.addEventListener(
        "change",
        {
            val file: File? = fileInput.files?.item(0)
            fileInput.value = "" // allow re-selecting the same file later (matches R-8's attachFileInput)
            if (file != null) {
                val description = descriptionInput.value?.trim()?.takeIf { it.isNotEmpty() }
                AppScope.launch {
                    uploadStatus.visible = true
                    uploadStatus.removeCssClass("kportal-status-error")
                    uploadStatus.content = t.projectFiles.uploading
                    when (val result = api.uploadProjectFile(projectId, file, file.name, description)) {
                        is ApiResult.Success -> {
                            uploadStatus.visible = false
                            descriptionInput.value = ""
                            reload()
                        }
                        is ApiResult.HttpError -> {
                            uploadStatus.addCssClass("kportal-status-error")
                            uploadStatus.content = t.errors.forCode(result.error?.code, result.status) ?: t.projectFiles.uploadFailed
                        }
                        ApiResult.NetworkError -> {
                            uploadStatus.addCssClass("kportal-status-error")
                            uploadStatus.content = t.common.networkErrorRetry
                        }
                    }
                }
            }
        },
    )

    indexLink.onClick {
        AppScope.launch {
            val markdown = api.projectFileIndexMarkdown(projectId)
            indexHost.removeAll()
            indexHost.removeCssClass("kportal-hidden")
            val pre = indexHost.div(className = "kportal-files-index-pre")
            pre.content = markdown ?: ""
        }
    }

    reload()

    return ProjectFilesPanelHandle(reload = { reload() })
}

private fun panelToggleLabel(
    expanded: Boolean,
    fileCount: Int,
): String {
    val arrow = if (expanded) "▾" else "▸" // ▾ / ▸, same glyphs the DSL toggle's chevron convention uses
    return "$arrow ${t.projectFiles.panelHeading} ($fileCount)"
}

private fun renderFileRow(
    host: Div,
    api: PortalApi,
    file: ProjectFileResponse,
    onChanged: () -> Unit,
) {
    val row = host.div(className = "kportal-file-row")
    if (ProjectFileKindClient.isImage(file.kind)) {
        val thumb = row.image(src = api.projectFileUrl(file.id), alt = file.fileName, className = "kportal-file-thumb")
        // `loading = "lazy"` -- decompression-bomb mitigation, defense-in-depth alongside the
        // server-side ProjectFileValidator.MAX_IMAGE_DIMENSION_PX cap: an image within the byte-size
        // cap can still decode to hundreds of MB in the browser (e.g. an 8192x8192 single-color
        // PNG/WebP compresses to well under 100 KiB but decodes to ~268 MB), and up to
        // Plan.maxProjectFiles thumbnails can be rendered in this panel at once on a Team plan. A
        // browser never decodes a `loading="lazy"` <img> until it is actually scrolled into view,
        // so an off-screen malicious thumbnail costs nothing until (if ever) the user scrolls to it
        // -- see ProjectFileValidator.MAX_IMAGE_DIMENSION_PX's KDoc for the matching server-side cap.
        thumb.setAttribute("loading", "lazy")
    }
    row.span(file.fileName, className = "kportal-file-name")
    row.span(" (${ProjectFileKindClient.sizeLabel(file.byteSize, t.format)})", className = "kportal-file-size")
    val descSpan = row.span(file.description ?: "", className = "kportal-file-desc")

    row.link(t.projectFiles.downloadLink, api.projectFileUrl(file.id), target = "_blank", className = "kportal-link")

    val editButton = row.button(t.projectFiles.editDescriptionButton, className = "kportal-icon-button")
    editButton.onClick { e ->
        e.stopPropagation()
        startInlineDescriptionEdit(row, descSpan, api, file, onChanged)
    }

    val deleteButton = row.button(t.projectFiles.deleteButton, className = "kportal-icon-button")
    val popoverHost = row.div()
    deleteButton.onClick { e ->
        e.stopPropagation()
        showFileDeleteConfirmation(popoverHost, api, file, onChanged)
    }
}

/** Mirrors `DiagramTabs.kt`'s `startInlineRename` -- Stift -> inline textfield, Enter/Blur
 * speichert per `PATCH`, Escape verwirft. */
private fun startInlineDescriptionEdit(
    row: Div,
    descSpan: io.kvision.html.Span,
    api: PortalApi,
    file: ProjectFileResponse,
    onChanged: () -> Unit,
) {
    val original = file.description.orEmpty()
    descSpan.visible = false
    val input = row.textInput(value = original, className = "kportal-input kportal-files-desc-input")
    input.maxlength = ProjectFileKindClient.MAX_DESCRIPTION_LENGTH
    var settled = false

    fun cancel() {
        if (settled) return
        settled = true
        input.getElement()?.remove()
        descSpan.visible = true
    }

    fun save() {
        if (settled) return
        val newDescription = input.value?.trim().orEmpty()
        if (newDescription == original) {
            cancel()
            return
        }
        settled = true
        AppScope.launch {
            when (api.updateProjectFileDescription(file.id, newDescription.ifEmpty { null })) {
                is ApiResult.Success, is ApiResult.HttpError, ApiResult.NetworkError -> onChanged()
            }
        }
    }

    input.setEventListener<TextInput> {
        keydown = { e ->
            when (e.key) {
                "Enter" -> {
                    e.preventDefault()
                    save()
                }
                "Escape" -> {
                    e.preventDefault()
                    cancel()
                }
            }
        }
        blur = { save() }
    }
}

/** Mirrors `DiagramTabs.kt`'s `showDeleteConfirmation` popover pattern -- the one confirmation
 * dialog shape used everywhere in this UI. */
private fun showFileDeleteConfirmation(
    host: Div,
    api: PortalApi,
    file: ProjectFileResponse,
    onChanged: () -> Unit,
) {
    host.removeAll()
    val popover = host.div(className = "kportal-popover")
    popover.onClick { e -> e.stopPropagation() }
    popover.span(t.projectFiles.deleteConfirmQuestion)
    val actions = popover.div(className = "kportal-popover-actions")
    val cancel = actions.button(t.projectFiles.cancelButton, className = "kportal-btn kportal-btn-secondary kportal-btn-auto")
    val confirm = actions.button(t.projectFiles.deleteButton, className = "kportal-btn kportal-btn-text")
    cancel.onClick { e ->
        e.stopPropagation()
        host.removeAll()
    }
    confirm.onClick { e ->
        e.stopPropagation()
        confirm.disabled = true
        cancel.disabled = true
        AppScope.launch {
            // DELETE is always 204/idempotent server-side (never confirms existence/ownership of a
            // foreign id) -- there is no HttpError/NetworkError branch worth distinguishing here,
            // reload() either shows the file gone or (on a transient network hiccup) unchanged.
            api.deleteProjectFile(file.id)
            onChanged()
        }
    }
}
